import React, { useCallback, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { TextField, Typography, Theme } from '@mui/material';
import { makeStyles, createStyles } from '@mui/styles';
import { Formik } from 'formik';

import { useIntl } from 'react-intl';

import ButtonWithLoader from '../../../../components/other/Buttons/ButtonWithLoader';

import { IAppState } from '../../../../store/rootDuck';
import { actions as cartActions } from '../../../../store/ducks/cart.duck';
import { actions as profileActions } from '../../../../store/ducks/profile.duck';
import { actions as promocodesActions } from '../../../../store/ducks/promocodes.duck';
import { actions as i18nActions } from '../../../../../_base/ducks/i18n';

import useCrudSnackbar from '../../../../hooks/useCrudSnackbar';
import { IUser, UserRoles } from '../../../../interfaces/user';

import homeStyles from '../../../../constants/homeStyles';
import TermDialog from '../../../../components/other/Dialog/TermDialog';
import { useTopUpBlance } from '../profile/hooks/useTopUpBlance';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    actions: {
      marginTop: theme.spacing(3),
    },
    lang: {
      display: 'flex',
      alignItems: 'center',
    },
    langImage: {
      width: '30px',
      height: '30px',
      borderRadius: '5px',
      overflow: 'hidden',
    },
    langTitle: {
      marginLeft: '5px',
    },
  })
);

interface IProps {
  me: IUser;
  isCart?: boolean;
}

const Bonuses: React.FC<TPropsFromRedux & IProps> = ({
  me,
  clearEdit,
  editSuccess,
  editLoading,
  editError,
  activate,
  edit,
  getOrCreateCart,
  userRoles,
  isAdmin,
  clearEditPromo,
  isCart,
}) => {
  const classes = useStyles();
  const homeClasses = homeStyles();
  const intl = useIntl();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [dialogContentType, setDialogContentType] = React.useState('');
  const [fetchBalance] = useTopUpBlance()

  const handleDialogOpen = useCallback((contentType: string) => {
    setDialogContentType(contentType);
    setOpenDialog(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setOpenDialog(false);
  }, []);

  useCrudSnackbar({
    success: editSuccess,
    error: editError,
    clear: clearEdit,
    successMessage: intl.formatMessage({ id: 'PROMOCODE.ACTIVATION.SUCCESS' }),
    errorMessage: `${intl.formatMessage({ id: 'ERROR' })}: ${editError}`,
    afterSuccessOrError: () => {
      clearEditPromo();
    },
    afterSuccess: () => {
      edit({
        login: me.login,
        email: me.email,
        fio: me.fio,
        // role: me.roles[0]
      });
      if (
        isCart &&
        userRoles &&
        !userRoles.includes(UserRoles.ROLE_VENDOR) &&
        !userRoles.includes(UserRoles.ROLE_VENDOR_STAFF) &&
        !isAdmin
      ) {
        getOrCreateCart();
      }
      clearEditPromo();
    },
  });

  useEffect(() => {
    return () => {
      clearEditPromo();
    };
  }, []);

  return (
    <Formik
      initialValues={{
        amount: '',
        paymentType: 'sbp'
      }}
      onSubmit={values => {
        fetchBalance(Number(values.amount), values.paymentType);
      }}
    >
      {({ values, errors, touched, handleBlur, handleSubmit, setFieldValue }) => (
        <form className={homeClasses.classes.form} onSubmit={handleSubmit}>
          <Typography>
            {isAdmin ? 
              intl.formatMessage({ id: 'USER.EDIT.BONUSES' }) : 
              intl.formatMessage({ id: 'USER.EDIT.DRIVER_CASH' })}: {me!.bonus_count}
          </Typography>

          <TextField
            type='number'
            style={{minWidth: 300}}
            label={isAdmin ? 
              intl.formatMessage({ id: 'USER.EDIT.PROMOCODE' }) : 
              intl.formatMessage({ id: 'USER.EDIT.CASH' })
            }
            margin='normal'
            name='amount'
            value={values.amount || null}
            variant='outlined'
            onBlur={handleBlur}
            onChange={e => {
              setFieldValue('amount', e.target.value);
            }}
            helperText={touched.amount && errors.amount}
            error={Boolean(touched.amount && errors.amount)}
          />

          <a
            onClick={() => handleDialogOpen('driver_offer')}
            rel='noopener noreferrer'
            className='kt-link'
          >
            {intl.formatMessage({ id: 'AUTH.GENERAL.PRIVACY.DRIVER' })}
          </a>

          <div className={classes.actions}>
            <ButtonWithLoader disabled={editLoading} loading={editLoading} type='submit'>
              {isAdmin ? 
                intl.formatMessage({ id: 'COMMON.BUTTON.APPLY' }) : 
                intl.formatMessage({ id: 'COMMON.BUTTON.DRIVER.APPLY' })
              }
            </ButtonWithLoader>
          </div>

          <TermDialog
            isOpen={openDialog}
            handleClose={handleDialogClose}
            contentType={dialogContentType}
          />
        </form>
      )}
    </Formik>
  );
};

const connector = connect(
  (state: IAppState) => ({
    editSuccess: state.promocodes.editSuccess,
    editLoading: state.promocodes.editLoading,
    editError: state.promocodes.editError,
    userRoles: state.auth.user?.roles,
    isAdmin: state.auth.user?.is_admin,
  }),
  {
    clearEdit: profileActions.clearEdit,
    clearEditPromo: promocodesActions.clearEdit,
    edit: profileActions.editRequest,
    activate: promocodesActions.activateRequest,
    setLanguage: i18nActions.setLanguage,
    getOrCreateCart: cartActions.getOrCreateRequest,
    fetchMe: profileActions.fetchRequest,
  }
);

type TPropsFromRedux = ConnectedProps<typeof connector>;

export default connector(Bonuses);
